import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';

/* Week-row calendar: multi-day events span as bars, overflow goes to "+N more". Phones get a day strip + agenda. */

export type CalStatus = 'live' | 'ready' | 'attention' | 'unknown';

export interface CalEvent {
    id: number;
    name: string;
    /** local calendar days (midnight timestamps), inclusive */
    startDay: number;
    endDay: number;
    /** e.g. "6p", "12:30p" — first day only */
    time?: string;
    status: CalStatus;
    /** "No devices assigned", "Live · 4m ago" … */
    note?: string;
    location?: string;
    href: string;
}

const DEFAULT_LANES = 3;
const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export const dayStart = (d: Date | number | string) => {
    const x = new Date(d);
    return new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
};
/** Last day an event occupies: a show ending before 6am belongs to the night it started. */
export const endDayOf = (end: Date | number | string, startDay: number) =>
    Math.max(startDay, dayStart(new Date(new Date(end).getTime() - 6 * 3600000)));
export const addDays = (t: number, n: number) => {
    const x = new Date(t);
    return new Date(x.getFullYear(), x.getMonth(), x.getDate() + n).getTime();
};
export const mondayOf = (t: number) => {
    const x = new Date(t);
    return addDays(dayStart(t), -((x.getDay() + 6) % 7));
};
export function shortTime(iso?: string) {
    if (!iso) return undefined;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return undefined;
    const h = d.getHours();
    const m = d.getMinutes();
    return `${h % 12 || 12}${m ? `:${String(m).padStart(2, '0')}` : ''}${h < 12 ? 'a' : 'p'}`;
}

/** "4:30p" → minutes since midnight, for ordering chips within a day */
const mins = (t?: string) => {
    const m = /^(\d{1,2})(?::(\d{2}))?([ap])$/.exec(t ?? '');
    if (!m) return 24 * 60;
    return ((+m[1] % 12) + (m[3] === 'p' ? 12 : 0)) * 60 + +(m[2] ?? 0);
};

const STATUS_LABEL: Record<CalStatus, string> = {
    live: 'Live',
    ready: 'Ready',
    attention: 'Needs attention',
    unknown: '',
};

function Dot({ status }: { status: CalStatus }) {
    return status === 'attention' ? (
        <span className="cal-dot attention" aria-hidden="true">
            ▲
        </span>
    ) : (
        <span className={`cal-dot ${status}`} aria-hidden="true" />
    );
}

interface Placed {
    ev: CalEvent;
    col: number;
    span: number;
    lane: number;
    fromBefore: boolean;
    toAfter: boolean;
}

function layoutWeek(events: CalEvent[], weekStart: number, maxLanes: number) {
    const weekEnd = addDays(weekStart, 6);
    const lanes: boolean[][] = Array.from({ length: maxLanes }, () => Array(7).fill(false));
    const placed: Placed[] = [];
    const hidden: CalEvent[][] = Array.from({ length: 7 }, () => []);
    const inWeek = events
        .filter((e) => e.endDay >= weekStart && e.startDay <= weekEnd)
        .sort(
            (a, b) =>
                a.startDay - b.startDay ||
                b.endDay - b.startDay - (a.endDay - a.startDay) ||
                mins(a.time) - mins(b.time),
        );
    for (const ev of inWeek) {
        const s = Math.max(ev.startDay, weekStart);
        const e = Math.min(ev.endDay, weekEnd);
        const col = Math.round((s - weekStart) / 86400000);
        const span = Math.round((e - s) / 86400000) + 1;
        const lane = lanes.findIndex((l) => l.slice(col, col + span).every((x) => !x));
        if (lane === -1) {
            for (let d = col; d < col + span; d++) hidden[d].push(ev);
            continue;
        }
        for (let d = col; d < col + span; d++) lanes[lane][d] = true;
        placed.push({ ev, col, span, lane, fromBefore: ev.startDay < weekStart, toAfter: ev.endDay > weekEnd });
    }
    return { placed, hidden };
}

export default function TwoWeekCalendar({
    events,
    start,
    onShift,
    onToday,
    navigate,
    weeks: weekCount = 2,
    lanes: maxLanes = DEFAULT_LANES,
    tall = false,
    month,
    title = 'Schedule · next 2 weeks',
    range,
    unit = 'week',
    isCurrent: isCurrentProp,
    toolbar,
}: {
    events: CalEvent[];
    /** Monday of the first week */
    start: number;
    onShift: (steps: number) => void;
    onToday: () => void;
    navigate: (href: string) => void;
    /** rows of weeks (2 on Home, 5–6 for a month, 1 for a week) */
    weeks?: number;
    /** event rows per week before "+N more" */
    lanes?: number;
    /** one week as seven columns of full cards (name, place, readiness) */
    tall?: boolean;
    /** month view: days outside this month (0–11) are dimmed */
    month?: number;
    title?: string;
    /** overrides the computed "Sep 28 – Oct 11" */
    range?: string;
    unit?: 'week' | 'month';
    isCurrent?: boolean;
    /** extra controls beside the arrows (e.g. Month | Week) */
    toolbar?: React.ReactNode;
}) {
    const today = dayStart(Date.now());
    const weeks = Array.from({ length: weekCount }, (_, i) => addDays(start, 7 * i));
    const end = addDays(start, 7 * weekCount - 1);
    const isCurrent = isCurrentProp ?? start === mondayOf(today);
    const fmt = (t: number, o: Intl.DateTimeFormatOptions) =>
        new Date(t).toLocaleDateString(undefined, o);
    const rangeLabel = range ?? `${fmt(start, { month: 'short', day: 'numeric' })} – ${fmt(end, {
        month: new Date(start).getMonth() === new Date(end).getMonth() ? undefined : 'short',
        day: 'numeric',
    })}`;

    /* "+N more" popover */
    const [more, setMore] = useState<{ day: number; list: CalEvent[] } | null>(null);
    const moreRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (!more) return;
        const close = (e: MouseEvent) => {
            if (!moreRef.current?.contains(e.target as Node)) setMore(null);
        };
        const esc = (e: KeyboardEvent) => e.key === 'Escape' && setMore(null);
        document.addEventListener('mousedown', close);
        window.addEventListener('keydown', esc);
        return () => {
            document.removeEventListener('mousedown', close);
            window.removeEventListener('keydown', esc);
        };
    }, [more]);

    /* phone: selected day */
    const inMonth = (d: number) => month === undefined || new Date(d).getMonth() === month;
    const days14 = Array.from({ length: 7 * weekCount }, (_, i) => addDays(start, i)).filter(inMonth);
    const firstDay = days14[0] ?? start;
    const lastDay = days14[days14.length - 1] ?? end;
    const [sel, setSel] = useState<number>(() => (today >= firstDay && today <= lastDay ? today : firstDay));
    useEffect(() => {
        setSel((cur) => (cur >= firstDay && cur <= lastDay ? cur : today >= firstDay && today <= lastDay ? today : firstDay));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [start, weekCount]);
    /* keep the selected day in view in the strip */
    const stripRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        const strip = stripRef.current;
        const on = strip?.querySelector<HTMLElement>('.cal-day.on');
        if (strip && on) strip.scrollLeft = Math.max(0, on.offsetLeft - strip.offsetLeft - 8);
    }, [sel, start]);
    const onDay = (d: number) =>
        events
            .filter((e) => e.startDay <= d && e.endDay >= d)
            .sort((a, b) => (a.startDay < d ? 0 : 1) - (b.startDay < d ? 0 : 1) || mins(a.time) - mins(b.time));

    const go = (href: string) => (e: React.MouseEvent) => {
        if (e.metaKey || e.ctrlKey) return;
        e.preventDefault();
        navigate(href);
    };
    const label = (ev: CalEvent) =>
        [ev.name, ev.time, ev.location, STATUS_LABEL[ev.status], ev.note].filter(Boolean).join(', ');

    return (
        <section className={`cal${tall ? ' tall' : ''}${month !== undefined ? ' month' : ''}`} aria-label={`${title}, ${rangeLabel}`}>
            <div className="cal-bar">
                {title && <p className="s-lab">{title}</p>}
                <span className="cal-range">{rangeLabel}</span>
                <span className="cal-nav">
                    {toolbar}
                    {!isCurrent && (
                        <button type="button" className="tb-btn" onClick={onToday}>
                            Today
                        </button>
                    )}
                    <button type="button" className="tb-btn tb-icon" aria-label={`Previous ${unit}`} title={`Previous ${unit}`} onClick={() => onShift(-1)}>
                        ‹
                    </button>
                    <button type="button" className="tb-btn tb-icon" aria-label={`Next ${unit}`} title={`Next ${unit}`} onClick={() => onShift(1)}>
                        ›
                    </button>
                </span>
            </div>

            {/* ---- week: seven columns of full cards ---- */}
            {tall && (
                <div className="cal-grid cal-wk">
                    {Array.from({ length: 7 }, (_, i) => {
                        const d = addDays(start, i);
                        const list = onDay(d);
                        return (
                            <div key={d} className={`cal-wcol${d === today ? ' today' : ''}${d < today ? ' past' : ''}${i >= 5 ? ' wknd' : ''}`}>
                                <div className={`cal-dh${d === today ? ' today' : ''}${d < today ? ' past' : ''}`}>
                                    <span className="dow">{DOW[i]}</span>
                                    <span className="dn">{new Date(d).getDate()}</span>
                                    {d === today && <span className="tag">Today</span>}
                                </div>
                                {list.length === 0 && <p className="cal-wnone">Nothing scheduled</p>}
                                {list.map((ev) => {
                                    const n = Math.round((ev.endDay - ev.startDay) / 86400000) + 1;
                                    const k = Math.round((d - ev.startDay) / 86400000) + 1;
                                    return (
                                        <a
                                            key={ev.id}
                                            href={ev.href}
                                            onClick={go(ev.href)}
                                            aria-label={label(ev)}
                                            className={`cal-card ${ev.status}${ev.endDay < today ? ' past' : ''}${n > 1 ? ' multi' : ''}`}
                                        >
                                            <span className="l1">
                                                <Dot status={ev.status} />
                                                <span className="t">{n > 1 ? `${k > 1 ? '' : `${ev.time ?? ''} · `}Day ${k} of ${n}` : ev.time}</span>
                                            </span>
                                            <span className="n">{ev.name}</span>
                                            {ev.location && <span className="m">{ev.location}</span>}
                                            {ev.note && ev.status !== 'unknown' && <span className={`m note ${ev.status}`}>{ev.note}</span>}
                                        </a>
                                    );
                                })}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* ---- desktop / tablet: the grid ---- */}
            <div className="cal-grid" hidden={tall}>
                {weeks.map((ws) => {
                    const { placed, hidden } = layoutWeek(events, ws, maxLanes);
                    return (
                        <div
                            className="cal-week"
                            key={ws}
                            style={{ gridTemplateRows: `30px repeat(${maxLanes}, 26px) 24px` }}
                        >
                            {Array.from({ length: 7 }, (_, i) => {
                                const d = addDays(ws, i);
                                return (
                                    <div
                                        key={`bg${d}`}
                                        className={`cal-cell${d === today ? ' today' : ''}${d < today ? ' past' : ''}${i >= 5 ? ' wknd' : ''}${inMonth(d) ? '' : ' out'}`}
                                        style={{ gridColumn: i + 1, gridRow: '1 / -1' }}
                                    />
                                );
                            })}
                            {Array.from({ length: 7 }, (_, i) => {
                                const d = addDays(ws, i);
                                const date = new Date(d);
                                return (
                                    <div key={`h${d}`} className={`cal-dh${d === today ? ' today' : ''}${d < today ? ' past' : ''}${inMonth(d) ? '' : ' out'}`} style={{ gridColumn: i + 1, gridRow: 1 }}>
                                        <span className="dow">{DOW[i]}</span>
                                        <span className="dn">{date.getDate() === 1 ? fmt(d, { month: 'short', day: 'numeric' }) : date.getDate()}</span>
                                        {d === today && <span className="tag">Today</span>}
                                    </div>
                                );
                            })}
                            {placed.map((p) => (
                                <a
                                    key={`${p.ev.id}-${ws}`}
                                    href={p.ev.href}
                                    onClick={go(p.ev.href)}
                                    aria-label={label(p.ev)}
                                    title={label(p.ev)}
                                    className={`cal-ev ${p.ev.status}${p.span > 1 || p.fromBefore ? ' multi' : ''}${p.fromBefore ? ' cont-l' : ''}${p.toAfter ? ' cont-r' : ''}${p.ev.endDay < today ? ' past' : ''}`}
                                    style={{ gridColumn: `${p.col + 1} / span ${p.span}`, gridRow: p.lane + 2 }}
                                >
                                    <Dot status={p.ev.status} />
                                    {p.ev.time && !p.fromBefore && <span className="t">{p.ev.time}</span>}
                                    <span className="n">{p.ev.name}</span>
                                    {(p.span > 1 || p.fromBefore) && (
                                        <span className="len">
                                            {Math.round((p.ev.endDay - p.ev.startDay) / 86400000) + 1} days
                                        </span>
                                    )}
                                </a>
                            ))}
                            {hidden.map((list, i) =>
                                list.length ? (
                                    <button
                                        key={`m${i}`}
                                        type="button"
                                        className="cal-more"
                                        style={{ gridColumn: i + 1, gridRow: maxLanes + 2 }}
                                        onClick={() => setMore({ day: addDays(ws, i), list: onDay(addDays(ws, i)) })}
                                    >
                                        +{list.length} more
                                    </button>
                                ) : null,
                            )}
                        </div>
                    );
                })}
                {more && (
                    <div className="menu cal-pop" ref={moreRef} role="dialog" aria-label={`Events on ${fmt(more.day, { weekday: 'long', month: 'short', day: 'numeric' })}`}>
                        <div className="mlab">{fmt(more.day, { weekday: 'long', month: 'short', day: 'numeric' })}</div>
                        {more.list.map((ev) => (
                            <a key={ev.id} className="menu-item cal-pop-item" href={ev.href} onClick={go(ev.href)}>
                                <Dot status={ev.status} />
                                <span className="t">{ev.time ?? ''}</span>
                                <span className="n">{ev.name}</span>
                            </a>
                        ))}
                    </div>
                )}
            </div>

            {/* ---- phone: day strip + agenda ---- */}
            <div className="cal-phone">
                <div className="cal-strip" role="tablist" aria-label="Days" ref={stripRef}>
                    {days14.map((d) => {
                        const n = onDay(d);
                        const date = new Date(d);
                        return (
                            <button
                                key={d}
                                type="button"
                                role="tab"
                                aria-selected={sel === d}
                                className={`cal-day${sel === d ? ' on' : ''}${d === today ? ' today' : ''}${d < today ? ' past' : ''}`}
                                onClick={() => setSel(d)}
                            >
                                <span className="dow">{DOW[(date.getDay() + 6) % 7]}</span>
                                <span className="dn">{date.getDate()}</span>
                                <span className="dots">
                                    {n.slice(0, 3).map((e) => (
                                        <span key={e.id} className={`cal-mini ${e.status}`} />
                                    ))}
                                </span>
                            </button>
                        );
                    })}
                </div>
                <p className="cal-sel">
                    {sel === today ? 'Today' : fmt(sel, { weekday: 'long' })} · {fmt(sel, { month: 'short', day: 'numeric' })}
                    {' · '}
                    {onDay(sel).length} {onDay(sel).length === 1 ? 'event' : 'events'}
                </p>
                <div className="tcard">
                    {onDay(sel).length === 0 ? (
                        <p className="cal-empty">Nothing scheduled.</p>
                    ) : (
                        onDay(sel).map((ev) => (
                            <a key={ev.id} className="cal-row" href={ev.href} onClick={go(ev.href)}>
                                <Dot status={ev.status} />
                                <span className="cal-row-main">
                                    <span className="n">{ev.name}</span>
                                    <span className="sub">
                                        {[
                                            ev.startDay < sel ? 'continues' : ev.time,
                                            ev.location,
                                            ev.endDay > ev.startDay ? `${Math.round((ev.endDay - ev.startDay) / 86400000) + 1} days` : '',
                                        ]
                                            .filter(Boolean)
                                            .join(' · ')}
                                    </span>
                                </span>
                                {ev.note && <span className={`cal-note ${ev.status}`}>{ev.note}</span>}
                            </a>
                        ))
                    )}
                </div>
            </div>
        </section>
    );
}

/** Legend used under the grid. */
export function CalLegend() {
    return (
        <p className="cal-legend">
            <span><Dot status="live" /> Live</span>
            <span><Dot status="ready" /> Ready</span>
            <span><Dot status="attention" /> Needs attention</span>
            <span><span className="cal-legend-bar" /> Multi-day</span>
        </p>
    );
}

export function useStableNow() {
    return useMemo(() => dayStart(Date.now()), []);
}
